import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { Confirmation } from '@abp/ng.theme.shared';
import { IdentityUserDto } from '@volo/abp.ng.identity/proxy';
import { DialogModule } from 'primeng/dialog';
import { RatingModule } from 'primeng/rating';
import { CxSelectComponent } from 'src/app/shared/components/cx-select/cx-select.component';
import { CrudService } from 'src/core/services/crud/crud.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { UserSkillDialogService } from 'src/core/services/user-skill/user-skill-dialog.service';
import { SkillService } from 'src/core/services/skill/skill.service';
import { SkillDto } from 'src/core/models/skill/skill.dto';
import { SkillUserAssignmentDto } from 'src/core/models/skill/skill-user-assignment.dto';
import { ListRequestDto } from 'src/core/models/request/list-request.dto';
import { SkillAssignment } from 'src/core/models/skill/skill-assigment.dto';
import { FormActionsComponent } from 'src/core/forms/form-actions/form-actions.component';

const PROFICIENCY_LABELS: Record<number, string> = {
  1: 'Skill::SkillManagement:Proficiency:Beginner',
  2: 'Skill::SkillManagement:Proficiency:Elementary',
  3: 'Skill::SkillManagement:Proficiency:Intermediate',
  4: 'Skill::SkillManagement:Proficiency:Advanced',
  5: 'Skill::SkillManagement:Proficiency:Expert',
};

@Component({
  selector: 'app-user-skill-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, LocalizationPipe, DialogModule, RatingModule, FormActionsComponent, CxSelectComponent],
  templateUrl: './user-skill-dialog.component.html',
})
export class UserSkillDialogComponent implements OnInit {
  private readonly dialogService = inject(UserSkillDialogService);
  private readonly crudService = inject(CrudService);
  private readonly skillService = inject(SkillService);
  private readonly toasterService = inject(ToasterService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  visible = false;
  user: IdentityUserDto | null = null;

  allSkills: SkillDto[] = [];
  activeSkills: SkillDto[] = [];
  assignments: SkillAssignment[] = [];
  selectedSkillId: number | null = null;

  loading = false;
  saving = false;

  get availableSkills(): SkillDto[] {
    const assignedIds = new Set(this.assignments.map(a => a.skillId));
    return this.activeSkills.filter(s => !assignedIds.has(s.id));
  }

  get canSave(): boolean {
    return (
      this.assignments.length === 0 ||
      this.assignments.every(a => (a.proficiencyLevel ?? 0) > 0)
    );
  }

  get hasInvalidLevelAssignment(): boolean {
    return this.assignments.some(a => (a.proficiencyLevel ?? 0) <= 0);
  }

  ngOnInit(): void {
    this.dialogService.open$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.user = user;
      this.selectedSkillId = null;
      this.assignments = [];
      this.visible = true;
      this.cdr.markForCheck();
      this.loadData();
    });
  }

  getProficiencyLabel(level: number | null): string {
    if (!level) {
      return '';
    }

    const key = PROFICIENCY_LABELS[level];
    return key ? this.localization.instant(key) : '';
  }

  trackByAssignment(_: number, a: SkillAssignment): number {
    return a.skillId;
  }

  onAdd(): void {
    if (!this.selectedSkillId) {
      return;
    }

    const skill = this.activeSkills.find(s => s.id === this.selectedSkillId);
    if (!skill) {
      return;
    }

    this.assignments = [
      ...this.assignments,
      { skillId: skill.id, skillName: skill.name, proficiencyLevel: 3 },
    ];
    this.selectedSkillId = null;
    this.cdr.markForCheck();
  }

  onRemove(index: number): void {
    const assignment = this.assignments[index];
    if (this.isLastAgentForSkill(assignment.skillId)) {
      this.confirmationService
        .warn(
          'Settings::WorkEnvironment:Skills:LastAgentMessage',
          'Settings::WorkEnvironment:Skills:LastAgentTitle',
        )
        .subscribe(status => {
          if (status === Confirmation.Status.confirm) {
            this.assignments = this.assignments.filter((_, i) => i !== index);
            this.cdr.markForCheck();
          }
        });
      return;
    }

    this.assignments = this.assignments.filter((_, i) => i !== index);
    this.cdr.markForCheck();
  }

  private isLastAgentForSkill(skillId: number): boolean {
    return this.allSkills.find(s => s.id === skillId)?.agentCount === 1;
  }

  onNavigateToSettings(): void {
    this.visible = false;
    this.router.navigate(['/setting-management'], {
      queryParams: { workEnvironmentTab: 'skills' },
    }).then(() => {
      setTimeout(() => {
        const tabLink = document.getElementById('Settings::WorkEnvironment:Title-tab');
        if (tabLink) {
          tabLink.click();
        }
      }, 300);
    });
  }

  onCancel(): void {
    this.visible = false;
  }

  onHide(): void {
    this.visible = false;
  }

  onSave(): void {
    if (!this.user || !this.canSave || this.saving) {
      return;
    }

    const userId = this.user.id;
    if (!userId) {
      return;
    }

    this.saving = true;

    const assignments: SkillUserAssignmentDto[] = this.assignments.map(assignment => ({
      skillId: assignment.skillId,
      level: assignment.proficiencyLevel ?? 0,
    }));

    this.skillService
      .updateUserAssignments(userId, assignments)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.toasterService.success('Skill::SkillManagement:SaveSuccess');
          this.dialogService.notifySkillsSaved();
          this.visible = false;
        },
      });
  }

  private loadData(): void {
    if (!this.user) {
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    const request: ListRequestDto = {
      skipCount: 0,
      maxResultCount: 1000,
      filters: [],
      sorters: [
        {
          field: 'name',
          direction: 'asc',
        },
      ],
    };

    const userId = this.user.id!.toLowerCase();

    this.crudService
      .get<SkillDto>(SkillDto, request)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: skills => {
          this.allSkills = skills.items ?? [];
          this.activeSkills = this.allSkills.filter(s => s.isActive);

          this.assignments = this.allSkills
            .map(skill => ({
              skill,
              assignment: (skill.assignedUsers ?? []).find(
                su => su.userId?.toLowerCase() === userId,
              ),
            }))
            .filter(entry => entry.assignment != null)
            .map(
              entry =>
                ({
                  skillId: entry.skill.id,
                  skillName: entry.skill.name,
                  proficiencyLevel: entry.assignment!.level,
                }) as SkillAssignment,
            );
        },
        error: () => {
          this.allSkills = [];
          this.activeSkills = [];
          this.assignments = [];
        },
      });
  }
}
